import { UserDataType } from "../Data/UserData";
import { LiveTrainSnapshot, LiveTrainStatus } from "./types";

const BASE_URL = "https://www.realtimetrains.co.uk";
const ENTRY_CRS = ["SOP", "LVC", "ORM", "HBL", "WKI", "NBN", "CTR", "ELP", "HNX"];
const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const SERVICE_CACHE_TTL_MS = 8 * 60 * MINUTE_MS;
const SERVICE_ROSTER_TTL_MS = 4 * 60 * MINUTE_MS;
const MAX_TRACKED_SERVICES = 180;
const MAX_NEW_SERVICE_FETCHES_PER_CYCLE = 36;
const REQUEST_SPACING_MS = 180;
const PARSED_SERVICE_CACHE_VERSION = 2;
const DEFAULT_PROXY_BASE_URL =
  "https://minimetro-rtt-proxy.cloudflare-a8e.workers.dev/api/rtt?url=";

type StationLookup = {
  byNormalizedName: Map<string, { stationId: number; stationName: string; x: number; y: number }>;
};

type DiscoveredService = {
  serviceUrl: string;
  departureTime?: string;
  destination?: string;
};

type ParsedStop = {
  crs?: string;
  stationName: string;
  platform?: string;
  plannedArrival?: string;
  plannedDeparture?: string;
  realtimeArrival?: string;
  realtimeDeparture?: string;
};

type ResolvedStop = ParsedStop & {
  stationId?: number;
  x?: number;
  y?: number;
  plannedArrivalMs?: number;
  plannedDepartureMs?: number;
  realtimeArrivalMs?: number;
  realtimeDepartureMs?: number;
};

type ParsedService = {
  serviceKey: string;
  serviceId: string;
  serviceUrl: string;
  origin: string;
  destination: string;
  stops: ParsedStop[];
};

type ParsedServiceCacheItem = {
  version: number;
  fetchedAtMs: number;
  service: ParsedService;
};

const parsedServiceCache = new Map<string, ParsedServiceCacheItem>();
const trackedServiceRoster = new Map<
  string,
  {
    service: DiscoveredService;
    lastSeenMs: number;
  }
>();

const normalizeStationName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\bliverpool\s+james\s+street\b/g, "james street")
    .replace(/\bst\.?\s*michaels\b/g, "st michaels")
    .replace(/\bstation\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

const sanitizeServiceUrl = (href: string): string => {
  const withoutHash = href.split("#")[0];
  return withoutHash.startsWith("http") ? withoutHash : `${BASE_URL}${withoutHash}`;
};

const readText = (element?: Element | null): string | undefined => {
  const text = element?.textContent?.trim();
  return text && text.length > 0 ? text : undefined;
};

const extractStationNameFromRow = (row: Element): string | undefined => {
  const nameAnchor =
    row.querySelector(".location a.name") ||
    row.querySelector("a.name") ||
    row.querySelector(".name");
  if (!nameAnchor) {
    return undefined;
  }

  const cloned = nameAnchor.cloneNode(true) as Element;
  const crsEl = cloned.querySelector(".crs");
  if (crsEl) {
    crsEl.remove();
  }
  return readText(cloned);
};

const extractHHMM = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const digits = value.replace(/\D/g, "");
  if (digits.length < 3) {
    return undefined;
  }
  const hhmm = digits.length === 3 ? `0${digits}` : digits.slice(0, 4);
  const hour = Number(hhmm.slice(0, 2));
  const minute = Number(hhmm.slice(2, 4));
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return undefined;
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return undefined;
  }
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2, 4)}`;
};

const toEpochMs = (
  serviceDate: string,
  hhmm?: string,
  previousEpochMs?: number
): number | undefined => {
  if (!hhmm) {
    return undefined;
  }
  let epochMs = new Date(`${serviceDate}T${hhmm}:00`).getTime();
  if (!Number.isFinite(epochMs)) {
    return undefined;
  }

  if (previousEpochMs !== undefined) {
    while (epochMs < previousEpochMs - 20 * MINUTE_MS) {
      epochMs += DAY_MS;
    }
  }
  return epochMs;
};

const toClockLabel = (epochMs?: number): string | undefined => {
  if (!epochMs) {
    return undefined;
  }
  const date = new Date(epochMs);
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const parseDepartureMinutes = (value?: string): number | undefined => {
  const hhmm = extractHHMM(value);
  if (!hhmm) {
    return undefined;
  }
  const [hours, minutes] = hhmm.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return undefined;
  }
  return hours * 60 + minutes;
};

const servicePriorityScore = (service: DiscoveredService): number => {
  const depMinutes = parseDepartureMinutes(service.departureTime);
  if (depMinutes === undefined) {
    return Number.POSITIVE_INFINITY;
  }
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  let delta = Math.abs(depMinutes - nowMinutes);
  if (delta > 12 * 60) {
    delta = 24 * 60 - delta;
  }
  return delta;
};

const fetchHtml = async (url: string): Promise<string> => {
  const customProxy = process.env.REACT_APP_RTT_PROXY?.trim();
  const proxyBase = customProxy && customProxy.length > 0 ? customProxy : DEFAULT_PROXY_BASE_URL;
  const proxyUrl = proxyBase.includes("{url}")
    ? proxyBase.replace("{url}", encodeURIComponent(url))
    : `${proxyBase}${encodeURIComponent(url)}`;
  const fallbackCandidates = [proxyUrl, url];
  let lastError: unknown = new Error("Unknown fetch error");

  for (const candidate of fallbackCandidates) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(candidate, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (response.status === 429 || response.status === 403) {
        throw new Error(`Rate limited by upstream (${response.status})`);
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} for ${candidate}`);
      }
      const html = await response.text();
      if (html.includes("Error 1015") && html.toLowerCase().includes("rate limited")) {
        throw new Error("Rate limited by proxy provider");
      }
      if (html && html.length > 100) {
        return html;
      }
    } catch (error) {
      lastError = error;
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastError;
};

const discoverServicesFromStationHtml = (html: string): DiscoveredService[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const serviceAnchors = Array.from(doc.querySelectorAll(".servicelist a.service")).filter(
    (element) => !element.classList.contains("ticket-upsell")
  );

  const results: DiscoveredService[] = [];
  for (const anchor of serviceAnchors) {
    const href = anchor.getAttribute("href");
    if (!href || !href.includes("/service/")) {
      continue;
    }
    const departureTime = readText(anchor.querySelector(".time"));
    const destination = readText(anchor.querySelector(".location span"));
    results.push({
      serviceUrl: sanitizeServiceUrl(href),
      departureTime,
      destination,
    });
  }
  return results;
};

const parseServicePage = (serviceUrl: string, html: string): ParsedService | undefined => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const rows = Array.from(doc.querySelectorAll(".locationlist .location.call.public"));
  if (rows.length < 2) {
    return undefined;
  }

  const stops: ParsedStop[] = [];
  for (const row of rows) {
    const stationName = extractStationNameFromRow(row);
    if (!stationName) {
      continue;
    }
    stops.push({
      crs: readText(row.querySelector(".crs")),
      stationName,
      platform: readText(row.querySelector(".platform")),
      plannedArrival: readText(row.querySelector(".gbtt.arr")),
      plannedDeparture: readText(row.querySelector(".gbtt.dep")),
      realtimeArrival: readText(row.querySelector(".realtime.arr")),
      realtimeDeparture: readText(row.querySelector(".realtime.dep")),
    });
  }

  if (stops.length < 2) {
    return undefined;
  }

  const serviceMatch = serviceUrl.match(/\/service\/gb-nr:([^/]+)\/(\d{4}-\d{2}-\d{2})/);
  const serviceId = serviceMatch?.[1] || serviceUrl;
  const serviceDate = serviceMatch?.[2] || "unknown-date";

  return {
    serviceKey: `${serviceId}-${serviceDate}`,
    serviceId,
    serviceUrl,
    origin: stops[0].stationName,
    destination: stops[stops.length - 1].stationName,
    stops,
  };
};

const buildStationLookup = (data: UserDataType): StationLookup => {
  const byNormalizedName = new Map<
    string,
    { stationId: number; stationName: string; x: number; y: number }
  >();

  for (const station of data.stations.values()) {
    const normalized = normalizeStationName(station.stationName);
    byNormalizedName.set(normalized, {
      stationId: Number(station.stationId),
      stationName: station.stationName,
      x: station.position[0],
      y: station.position[1],
    });
  }

  return { byNormalizedName };
};

const resolveServiceToLiveTrain = (
  parsedService: ParsedService,
  lookup: StationLookup
): LiveTrainStatus | undefined => {
  const serviceDateMatch = parsedService.serviceUrl.match(/\/(\d{4}-\d{2}-\d{2})(?:$|\/)/);
  const serviceDate = serviceDateMatch?.[1];
  if (!serviceDate) {
    return undefined;
  }

  const resolvedStops: ResolvedStop[] = [];
  let previousPlannedMs: number | undefined;
  let previousRealtimeMs: number | undefined;

  for (const stop of parsedService.stops) {
    const normalized = normalizeStationName(stop.stationName);
    const mapped = lookup.byNormalizedName.get(normalized);
    const plannedArrivalMs = toEpochMs(
      serviceDate,
      extractHHMM(stop.plannedArrival),
      previousPlannedMs
    );
    if (plannedArrivalMs !== undefined) {
      previousPlannedMs = plannedArrivalMs;
    }
    const plannedDepartureMs = toEpochMs(
      serviceDate,
      extractHHMM(stop.plannedDeparture),
      previousPlannedMs
    );
    if (plannedDepartureMs !== undefined) {
      previousPlannedMs = plannedDepartureMs;
    }

    const realtimeArrivalMs = toEpochMs(
      serviceDate,
      extractHHMM(stop.realtimeArrival),
      previousRealtimeMs
    );
    if (realtimeArrivalMs !== undefined) {
      previousRealtimeMs = realtimeArrivalMs;
    }
    const realtimeDepartureMs = toEpochMs(
      serviceDate,
      extractHHMM(stop.realtimeDeparture),
      previousRealtimeMs
    );
    if (realtimeDepartureMs !== undefined) {
      previousRealtimeMs = realtimeDepartureMs;
    }

    resolvedStops.push({
      ...stop,
      stationId: mapped?.stationId,
      x: mapped?.x,
      y: mapped?.y,
      plannedArrivalMs,
      plannedDepartureMs,
      realtimeArrivalMs,
      realtimeDepartureMs,
    });
  }

  const firstStop = resolvedStops[0];
  const lastStop = resolvedStops[resolvedStops.length - 1];
  const serviceStartMs =
    firstStop.realtimeDepartureMs ||
    firstStop.plannedDepartureMs ||
    firstStop.realtimeArrivalMs ||
    firstStop.plannedArrivalMs;
  const serviceEndMs =
    lastStop.realtimeArrivalMs ||
    lastStop.plannedArrivalMs ||
    lastStop.realtimeDepartureMs ||
    lastStop.plannedDepartureMs;

  if (!serviceStartMs || !serviceEndMs) {
    return undefined;
  }

  const now = Date.now();
  if (now < serviceStartMs - 10 * MINUTE_MS || now > serviceEndMs + 12 * MINUTE_MS) {
    return undefined;
  }

  for (let i = 0; i < resolvedStops.length; i += 1) {
    const stop = resolvedStops[i];
    if (!stop.stationId || stop.x === undefined || stop.y === undefined) {
      continue;
    }

    const arrivalMs =
      stop.realtimeArrivalMs ||
      stop.plannedArrivalMs ||
      stop.realtimeDepartureMs ||
      stop.plannedDepartureMs;
    const departureMs =
      stop.realtimeDepartureMs ||
      stop.plannedDepartureMs ||
      stop.realtimeArrivalMs ||
      stop.plannedArrivalMs;
    if (!arrivalMs || !departureMs) {
      continue;
    }

    if (now >= arrivalMs - 20 * 1000 && now <= departureMs + 20 * 1000) {
      const next = resolvedStops.slice(i + 1).find((candidate) => candidate.stationId);
      const direction: 1 | -1 =
        next && next.x !== undefined && next.x < stop.x ? -1 : 1;
      return {
        id: parsedService.serviceKey,
        serviceId: parsedService.serviceId,
        serviceUrl: parsedService.serviceUrl,
        origin: parsedService.origin,
        destination: parsedService.destination,
        platform: stop.platform,
        expectedTime: toClockLabel(departureMs),
        expectedEpochMs: departureMs,
        phase: "AtStation",
        currentStation: stop.stationName,
        nextStation: next?.stationName,
        direction,
        x: stop.x,
        y: stop.y,
        status: `At ${stop.stationName}`,
        label: parsedService.destination,
        segmentStartMs: arrivalMs,
        segmentEndMs: departureMs,
        segmentFromX: stop.x,
        segmentFromY: stop.y,
        segmentToX: next?.x ?? stop.x,
        segmentToY: next?.y ?? stop.y,
      };
    }
  }

  for (let i = 0; i < resolvedStops.length - 1; i += 1) {
    const from = resolvedStops[i];
    const to = resolvedStops[i + 1];
    if (
      !from.stationId ||
      !to.stationId ||
      from.x === undefined ||
      from.y === undefined ||
      to.x === undefined ||
      to.y === undefined
    ) {
      continue;
    }

    const departureMs =
      from.realtimeDepartureMs ||
      from.plannedDepartureMs ||
      from.realtimeArrivalMs ||
      from.plannedArrivalMs;
    const arrivalMs =
      to.realtimeArrivalMs ||
      to.plannedArrivalMs ||
      to.realtimeDepartureMs ||
      to.plannedDepartureMs;
    if (!departureMs || !arrivalMs || arrivalMs <= departureMs) {
      continue;
    }
    if (now < departureMs || now > arrivalMs) {
      continue;
    }

    const progress = Math.max(
      0,
      Math.min(1, (now - departureMs) / Math.max(1, arrivalMs - departureMs))
    );
    const x = from.x + (to.x - from.x) * progress;
    const y = from.y + (to.y - from.y) * progress;
    const direction: 1 | -1 = to.x >= from.x ? 1 : -1;
    return {
      id: parsedService.serviceKey,
      serviceId: parsedService.serviceId,
      serviceUrl: parsedService.serviceUrl,
      origin: parsedService.origin,
      destination: parsedService.destination,
      platform: to.platform || from.platform,
      expectedTime: toClockLabel(arrivalMs),
      expectedEpochMs: arrivalMs,
      phase: "BetweenStations",
      currentStation: from.stationName,
      nextStation: to.stationName,
      direction,
      x,
      y,
      status: `${from.stationName} → ${to.stationName}`,
      label: parsedService.destination,
      segmentStartMs: departureMs,
      segmentEndMs: arrivalMs,
      segmentFromX: from.x,
      segmentFromY: from.y,
      segmentToX: to.x,
      segmentToY: to.y,
    };
  }

  return undefined;
};

const discoverServiceUrls = async (): Promise<DiscoveredService[]> => {
  const stationUrls = ENTRY_CRS.map(
    (crs) => `${BASE_URL}/search/simple/gb-nr:${crs}`
  );
  const batches: DiscoveredService[] = [];

  await Promise.all(
    stationUrls.map(async (stationUrl) => {
      try {
        const html = await fetchHtml(stationUrl);
        batches.push(...discoverServicesFromStationHtml(html));
      } catch (error) {
        console.error(error);
      }
    })
  );

  const uniqueByUrl = new Map<string, DiscoveredService>();
  for (const service of batches) {
    uniqueByUrl.set(service.serviceUrl, service);
  }
  return Array.from(uniqueByUrl.values())
    .sort((a, b) => servicePriorityScore(a) - servicePriorityScore(b))
    .slice(0, Math.min(MAX_TRACKED_SERVICES, 120));
};

const updateTrackedServiceRoster = (
  discoveredServices: DiscoveredService[]
): DiscoveredService[] => {
  const now = Date.now();

  for (const service of discoveredServices) {
    trackedServiceRoster.set(service.serviceUrl, {
      service,
      lastSeenMs: now,
    });
  }

  for (const [serviceUrl, tracked] of trackedServiceRoster.entries()) {
    if (now - tracked.lastSeenMs > SERVICE_ROSTER_TTL_MS) {
      trackedServiceRoster.delete(serviceUrl);
    }
  }

  return Array.from(trackedServiceRoster.values())
    .sort((a, b) => {
      const seenDelta = b.lastSeenMs - a.lastSeenMs;
      if (seenDelta !== 0) {
        return seenDelta;
      }
      return servicePriorityScore(a.service) - servicePriorityScore(b.service);
    })
    .slice(0, MAX_TRACKED_SERVICES)
    .map((tracked) => tracked.service);
};

const pruneParsedServiceCache = (): void => {
  const now = Date.now();
  for (const [serviceUrl, entry] of parsedServiceCache.entries()) {
    if (now - entry.fetchedAtMs > SERVICE_CACHE_TTL_MS) {
      parsedServiceCache.delete(serviceUrl);
    }
  }
};

export const crawlMerseyrailLiveTrains = async (
  data: UserDataType
): Promise<LiveTrainSnapshot> => {
  pruneParsedServiceCache();
  const lookup = buildStationLookup(data);
  const discoveredServices = await discoverServiceUrls();
  const candidateServices = updateTrackedServiceRoster(discoveredServices);
  const parsedServices: ParsedService[] = [];
  let remainingNewFetchBudget = MAX_NEW_SERVICE_FETCHES_PER_CYCLE;

  for (const service of candidateServices) {
    const cached = parsedServiceCache.get(service.serviceUrl);
    if (cached && cached.version === PARSED_SERVICE_CACHE_VERSION) {
      parsedServices.push(cached.service);
      continue;
    }
    if (remainingNewFetchBudget < 1) {
      continue;
    }

    remainingNewFetchBudget -= 1;
    try {
      const serviceHtml = await fetchHtml(service.serviceUrl);
      const parsedService = parseServicePage(service.serviceUrl, serviceHtml);
      if (!parsedService) {
        continue;
      }
      parsedServiceCache.set(service.serviceUrl, {
        version: PARSED_SERVICE_CACHE_VERSION,
        fetchedAtMs: Date.now(),
        service: parsedService,
      });
      parsedServices.push(parsedService);
      await wait(REQUEST_SPACING_MS);
    } catch (error) {
      console.error(error);
    }
  }

  const uniqueParsedById = new Map<string, ParsedService>();
  for (const parsedService of parsedServices) {
    uniqueParsedById.set(parsedService.serviceKey, parsedService);
  }

  const trains: LiveTrainStatus[] = [];
  for (const parsedService of uniqueParsedById.values()) {
    const resolved = resolveServiceToLiveTrain(parsedService, lookup);
    if (resolved) {
      trains.push(resolved);
    }
  }

  trains.sort((a, b) => a.serviceId.localeCompare(b.serviceId));

  return {
    updatedAtMs: Date.now(),
    trains,
    serviceCount: candidateServices.length,
  };
};
